// Styles
import { concatStyleSets, FontSizes, memoizeFunction, mergeStyles } from "@fluentui/react";
import { flexParent, flexParentCenterAll, textClip } from "../../../utilities/Mixin.styles";
// Constants
import { Colors } from "@ms/uno-constants/lib/local/Colors";
import { PlannerTheme } from "@ms/uno-constants/lib/local/ThemeConstants";
import { BasicPlanLabelConstants } from "../../../constants/BasicPlanLabelConstants";
import { LoopBorderRadius } from "../../../constants/StyleConstants";
// Utilities
import { numberToPx, pxToNumber } from "@ms/uno-utilities/lib/local/StyleUtilities";
import { isLoopTheme } from "../../../utilities/ThemingUtilities";
/**
 * Label Tag Style constants
 */ const labelContentHorizontalPadding = "8px";
const labelContentVerticalPadding = "2px";
const labelBorderWidth = "1px";
const labelHeight = "20px";
const labelTagHeight = numberToPx(pxToNumber(labelHeight) - pxToNumber(labelContentVerticalPadding) * 2);
const labelPaddingCollapsed = `${labelContentVerticalPadding} ${labelContentHorizontalPadding}`;
const labelPadding = `${numberToPx(pxToNumber(labelContentVerticalPadding) - pxToNumber(labelBorderWidth))} ${numberToPx(pxToNumber(labelContentHorizontalPadding) - pxToNumber(labelBorderWidth))}`;
/**
 * Helper to get styles for the Label Tag
 * @param theme App context theme
 * @param index Label color index
 * @param isCollapsed Whether the label is collapsed
 * @param showBorder Whether the label should have a border
 * @param isHovered Whether the label is hovered on
 * @param customStyles Any custom style overrides
 */ export const getStyles = memoizeFunction((theme, index, isCollapsed, showBorder, isHovered, customStyles)=>{
    const isLoop = isLoopTheme(theme);
    // Label colors
    const backgroundColor = BasicPlanLabelConstants.LabelFormattingData[index].backgroundColor;
    const hoverBackgroundColor = BasicPlanLabelConstants.LabelFormattingData[index].hoverBackgroundColor;
    const borderColor = BasicPlanLabelConstants.LabelFormattingData[index].hoverBorderColor;
    const foregroundColor = BasicPlanLabelConstants.LabelFormattingData[index].textColor;
    const LabelTagStyles = {
        root: [
            flexParent(),
            flexParentCenterAll,
            {
                fontSize: FontSizes.small,
                padding: isCollapsed ? labelPaddingCollapsed : labelPadding,
                width: isCollapsed ? "4px" : undefined,
                height: isCollapsed ? "4px" : labelTagHeight,
                lineHeight: labelTagHeight,
                minWidth: numberToPx(pxToNumber(labelHeight) - pxToNumber(labelContentHorizontalPadding) * 2),
                maxWidth: "112px",
                borderRadius: isLoop ? LoopBorderRadius : "4px",
                border: `solid ${labelBorderWidth}`,
                // Label colors
                backgroundColor: isHovered && isCollapsed ? hoverBackgroundColor : backgroundColor,
                borderColor: isCollapsed || showBorder ? borderColor : "transparent",
                color: foregroundColor
            },
            theme.name === PlannerTheme.HighContrast && {
                backgroundColor: `${Colors.Black} !important`,
                borderColor: `${Colors.White} !important`,
                ":hover": {
                    borderColor: `${Colors.HighContrastYellow} !important`
                }
            }
        ],
        labelTitle: [
            textClip("ellipsis"),
            {
                alignItems: "center",
                pointerEvents: "none"
            },
            theme.name === PlannerTheme.HighContrast && {
                color: Colors.White
            }
        ],
        labelIcon: [
            {
                "> i": {
                    marginLeft: "0px",
                    marginRight: "2px"
                }
            }
        ]
    };
    return concatStyleSets(LabelTagStyles, customStyles);
});
/**
 * Get the classnames associated with the computed styles for the Label Tag component
 * @param styles The styles for the component
 */ export const getClassNames = memoizeFunction((styles)=>{
    return {
        root: mergeStyles("labelTag", styles.root),
        labelTitle: mergeStyles("labelTitle", styles.labelTitle),
        labelIcon: mergeStyles("labelIcon", styles.labelIcon)
    };
});
